// Desktop ECON uses TradingView's full calendar page so Electron can capture
// an event into the Desk Plan. Web and mobile use TradingView's supported
// economic-calendar widget, which is reliable in a browser frame.
// [claude-code 2026-04-18] S24-T4: Admin surface now wraps Refinement/Approvals/Monitor via AdminShell
// [claude-code 2026-04-03] Extracted from MainLayout.tsx — tab content rendering
// [claude-code 2026-04-30] RiskFlow tab accepts the shared Ask AI catalyst callback.
import React, {
  Suspense,
  lazy,
  useEffect,
  useState,
  type ComponentType,
} from "react";
import { NarrativeProvider } from "../../contexts/NarrativeContext";
import { fetchNarrativeSessions } from "../../lib/narrative-session-api";
import type { NarrativeSessionSummary } from "../narrative/NarrativeSessionHistory";
import type { SurfaceCapabilities } from "../../lib/surface-capabilities";
import { useAuth } from "../../contexts/AuthContext";
import { EntitlementLock } from "../paywall/EntitlementLock";

function lazyNamed<T extends Record<string, ComponentType<any>>>(
  loader: () => Promise<T>,
  name: keyof T,
) {
  return lazy(async () => ({ default: (await loader())[name] }));
}

const AdminShell = lazyNamed(() => import("../admin/AdminShell"), "AdminShell");
const ApparatusMap = lazyNamed(
  () => import("../apparatus/ApparatusMap"),
  "ApparatusMap",
);
const ConsiliumHub = lazyNamed(
  () => import("../consilium/ConsiliumHub"),
  "ConsiliumHub",
);
const ConsiliumLite = lazyNamed(
  () => import("../consilium/ConsiliumLite"),
  "ConsiliumLite",
);
const DeskMap = lazyNamed(() => import("../narrative/DeskMap"), "DeskMap");
const DeskOpsPage = lazyNamed(() => import("./DeskOpsPage"), "DeskOpsPage");
const DeskRail = lazyNamed(() => import("../desk/DeskRail"), "DeskRail");
const MainDashboard = lazyNamed(
  () => import("../executive/MainDashboard"),
  "MainDashboard",
);
const PerformanceJournal = lazyNamed(
  () => import("../journal/PerformanceJournal"),
  "PerformanceJournal",
);
const RiskFlowMain = lazyNamed(
  () => import("../feed/RiskFlowMain"),
  "RiskFlowMain",
);
const ScoutsPage = lazyNamed(
  () => import("../scouts/ScoutsPage"),
  "ScoutsPage",
);
const SettingsPage = lazyNamed(
  () => import("../SettingsPanel"),
  "SettingsPage",
);
const TradingViewCalendar = lazyNamed(
  () => import("../econ/TradingViewCalendar"),
  "TradingViewCalendar",
);
const TradingViewEconCalendarEmbed = lazyNamed(
  () => import("../econ/TradingViewEconCalendarEmbed"),
  "TradingViewEconCalendarEmbed",
);

type NavTab =
  | "feed"
  | "analysis"
  | "riskflow"
  | "dashboard"
  | "econ"
  | "narrative"
  | "apparatus"
  | "scouts"
  | "performance"
  | "proposals"
  | "desk-ops"
  | "settings";

interface TabRendererProps {
  activeTab: NavTab;
  tabTransitioning: boolean;
  prevTab: NavTab | null;
  showRefinement: boolean;
  navigateTab: (tab: NavTab) => void;
  onChatAlert?: (alert: {
    headline: string;
    summary?: string | null;
    source?: string;
    ivScore?: number | null;
    publishedAt?: string;
  }) => void;
  capabilities: SurfaceCapabilities;
}

export function TabRenderer({
  activeTab,
  tabTransitioning,
  prevTab,
  showRefinement,
  navigateTab,
  onChatAlert,
  capabilities,
}: TabRendererProps) {
  const { entitlements } = useAuth();
  const [hasMountedEcon, setHasMountedEcon] = useState(activeTab === "econ");
  const animClass =
    tabTransitioning && prevTab
      ? "animate-fade-out-tab"
      : "animate-fade-in-tab";

  useEffect(() => {
    if (activeTab === "econ") setHasMountedEcon(true);
  }, [activeTab]);

  return (
    <div className="flex-1 min-h-0 overflow-hidden">
      <Suspense fallback={<TabFallback />}>
        {showRefinement && (
          <div key="admin" className="h-full w-full animate-fade-in-tab">
            <AdminShell />
          </div>
        )}
        {!showRefinement && activeTab === "dashboard" && (
          <div
            key="dashboard"
            data-tour-target="dashboard"
            className={`h-full w-full section-fade-corners ${animClass}`}
          >
            <MainDashboard
              onNavigateTab={(tab) => navigateTab(tab as NavTab)}
              deskSecondPageMode={capabilities.deskSecondPageMode}
            />
          </div>
        )}
        {!showRefinement && activeTab === "analysis" && (
          <div
            key="analysis"
            data-tour-target="chat"
            className={`h-full w-full section-fade-corners ${animClass}`}
          >
            {capabilities.consiliumMode === "full" ? (
              <ConsiliumHub />
            ) : (
              <ConsiliumLite />
            )}
          </div>
        )}
        {!showRefinement && activeTab === "riskflow" && (
          <div
            key="riskflow"
            data-tour-target="riskflow"
            className={`h-full w-full section-fade-corners ${animClass}`}
          >
            <RiskFlowMain onChatAlert={onChatAlert} />
          </div>
        )}
        {!showRefinement && (activeTab === "econ" || hasMountedEcon) && (
          <div
            key="econ"
            data-tour-target="econ"
            className={`h-full w-full ${activeTab === "econ" ? animClass : "hidden"}`}
          >
            {capabilities.isElectronDesktop ? (
              <TradingViewCalendar />
            ) : (
              <TradingViewEconCalendarEmbed />
            )}
          </div>
        )}
        {!showRefinement && activeTab === "narrative" && (
          <div
            key="narrative"
            data-tour-target="narrative"
            className={`h-full w-full ${animClass}`}
          >
            <NarrativeProvider>
              <DeskNarrativeMapSurface />
            </NarrativeProvider>
          </div>
        )}
        {!showRefinement && activeTab === "apparatus" && (
          <div
            key="apparatus"
            data-tour-target="apparatus"
            className={`h-full w-full ${animClass}`}
          >
            <ApparatusMap />
          </div>
        )}
        {!showRefinement && activeTab === "scouts" && (
          <div key="scouts" className={`h-full w-full ${animClass}`}>
            <ScoutsPage />
          </div>
        )}
        {!showRefinement && activeTab === "proposals" && (
          <div
            key="proposals"
            data-tour-target="desk-rail"
            className={`h-full w-full ${animClass}`}
          >
            {!entitlements["desk.plan"] ? (
              <EntitlementLock
                eyebrow="Desk"
                title="Desk is Coming Soon"
                detail="Desk feature listings stay visible for launch, but there is no price or purchase path yet."
              />
            ) : capabilities.allowDesktopRail ? (
              <DeskRail />
            ) : (
              <MainDashboard
                onNavigateTab={(tab) => navigateTab(tab as NavTab)}
                deskSecondPageMode={capabilities.deskSecondPageMode}
              />
            )}
          </div>
        )}
        {!showRefinement && activeTab === "performance" && (
          <div
            key="performance"
            data-tour-target="performance"
            className={`h-full w-full ${animClass}`}
          >
            {entitlements["performance.access"] &&
            capabilities.allowPerformance ? (
              <PerformanceJournal />
            ) : (
              <EntitlementLock
                eyebrow="Performance"
                title="Performance unlocks with Analyst"
                detail="Performance is available to trialing or active Analyst users through backend entitlement truth."
              />
            )}
          </div>
        )}
        {!showRefinement && activeTab === "settings" && (
          <div key="settings" className={`h-full w-full ${animClass}`}>
            <SettingsPage capabilities={capabilities} />
          </div>
        )}
        {!showRefinement && activeTab === "desk-ops" && (
          <div key="desk-ops" className={`h-full w-full ${animClass}`}>
            <DeskOpsPage />
          </div>
        )}
      </Suspense>
    </div>
  );
}

function TabFallback() {
  return <div className="fintheon-tab-fallback-surface h-full w-full" />;
}

function DeskNarrativeMapSurface() {
  const [sessions, setSessions] = useState<NarrativeSessionSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchNarrativeSessions()
      .then((items) => {
        if (cancelled) return;
        setSessions(items);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setSessions([]);
        setError(
          err instanceof Error
            ? err.message
            : "Narrative sessions failed to load.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="relative h-full w-full">
      <DeskMap sessions={sessions} activeSessionId={null} />
      {error ? (
        <div className="fintheon-popover-surface pointer-events-none absolute left-4 top-4 z-50 max-w-sm px-3 py-2 text-xs text-[var(--fintheon-muted)]">
          {error}
        </div>
      ) : null}
    </div>
  );
}
